import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, LayoutGrid } from 'lucide-react';
import { Button } from '../ui/Button';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../ui/Drawer';
import { Select, SelectContent, SelectIcon, SelectItem, SelectTrigger, SelectValue } from '../ui/Select/Select';
import { useUser } from '../UserContext';
import { useSubMenu } from '../SubMenuContext';
import { ToolbarEchoGate } from './ToolbarEchoGate';
import { ToolbarNotificationsBroadcast } from './ToolbarNotificationsBroadcast';
import { ToolbarNotificationsPanel } from './ToolbarNotificationsPanel';
import type { ToolbarAppsConfig, ToolbarNotification, ToolbarPlusAppEntry } from './notificationTypes';
import { dedupeNotificationsById } from './notificationModel';
import { getBroadcastChannelAppKey } from './broadcastChannelAppSlug';
import { resolveToolbarNotificationsConnection } from './toolbarNotificationsConnection';
import SkelletonLoader from '../ui/Skelleton';

const PER_PAGE = 50;

const SKELETON_ROW_WIDTHS: [string, string, string][] = [
    ['88%', '72%', '38%'],
    ['92%', '64%', '44%'],
    ['78%', '70%', '36%'],
    ['85%', '58%', '42%'],
    ['90%', '76%', '40%'],
];

const NOTIFICATIONS_LIST_MOTION = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const },
};

function NotificationsListSkeleton({ rows = 15 }: { rows?: number }) {
    return (
        <section className='overflow-hidden bg-white text-gray-900 shadow-sm' aria-busy='true' aria-label='Carregando notificações'>
            <ul className='divide-y divide-gray-100'>
                {Array.from({ length: rows }, (_, i) => {
                    const [w1, w2, w3] = SKELETON_ROW_WIDTHS[i % SKELETON_ROW_WIDTHS.length]!;
                    return (
                        <li key={i}>
                            <div className='flex gap-3 px-4 py-3.5'>
                                <div className='relative shrink-0'>
                                    <div className='relative size-11 shrink-0 overflow-hidden rounded-2xl bg-gray-200'>
                                        <div className='h-full w-full bg-gradient-to-r from-transparent via-gray-300 to-transparent -translate-x-full animate-[shimmer_2s_infinite]' />
                                    </div>
                                </div>
                                <div className='min-w-0 flex-1 space-y-2 pt-0.5'>
                                    <SkelletonLoader width={w1} className='h-3.5' />
                                    <SkelletonLoader width={w2} className='h-3.5' />
                                    <SkelletonLoader width={w3} className='h-3' />
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

/** Radix Select does not allow empty string as item value; maps to no API filter */
const FILTER_ALL_VALUE = '__all_apps__';

function notificationsListQuery(filterApplicationCode: string): string {
    const code = filterApplicationCode ?? '';
    return `?filter[empreender_plus_application_code]=${encodeURIComponent(code)}&sort=-id&per_page=${PER_PAGE}`;
}

function notificationsApiHeaders(bearerToken: string): HeadersInit {
    return {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${bearerToken}`,
    };
}

async function parseNotificationsApiJson<T>(res: Response): Promise<T> {
    const text = await res.text();
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
        const isHtml = text.trimStart().startsWith('<');
        throw new Error(isHtml ? `A API devolveu HTML em vez de JSON (HTTP ${res.status}). Confirme VITE_APP_URL e o token Bearer.` : `Resposta inválida da API (HTTP ${res.status}).`);
    }
    try {
        return JSON.parse(text) as T;
    } catch {
        throw new Error(`Resposta JSON inválida (HTTP ${res.status}).`);
    }
}

/**
 * Bell + drawer lateral com ToolbarNotificationsPanel.
 * Token/id: UserContext a partir de hub/extractHubSessionForNotifications.ts; se não houver token no user, usa Authorization em SubMenuProvider.headers.
 */
export function AppNotificationsDrawer() {
    const { user, hideApps } = useUser();
    const { app } = useSubMenu();

    const [open, setOpen] = useState(false);
    const [filterApp, setFilterApp] = useState('');
    const [notifications, setNotifications] = useState<ToolbarNotification[]>([]);
    const [listStatus, setListStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
    const [listError, setListError] = useState<string | null>(null);
    const [unreadCount, setUnreadCount] = useState<number | null>(null);

    const channelAppKey = useMemo(() => getBroadcastChannelAppKey(app), [app]);

    const bearerToken = useMemo(() => {
        const fromUser = user?.plus_token?.trim();
        if (fromUser) return fromUser;
        return null;
    }, [user?.plus_token]);

    const userId = user?.empreender_plus_user_id ?? null;

    const uid = userId === null || userId === undefined || String(userId).trim() === '' ? null : Number.isNaN(Number(userId)) ? userId : Number(userId);

    const { apiBaseUrl, echoParams } = useMemo(() => resolveToolbarNotificationsConnection(bearerToken), [bearerToken]);

    const appsConfig = (user?.apps ?? {}) as ToolbarAppsConfig;

    const appFilterOptions = useMemo(() => {
        const entries = Object.entries(appsConfig) as [string, ToolbarPlusAppEntry][];
        return entries
            .map(([code, cfg]) => {
                const raw = cfg?.img?.trim();
                return {
                    code,
                    label: cfg?.faq?.name?.trim() || code,
                    iconSrc: raw && raw !== '' ? raw : null,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
    }, [appsConfig]);

    const effectiveFilterApp = useMemo(() => (filterApp !== '' && appFilterOptions.some((o) => o.code === filterApp) ? filterApp : ''), [filterApp, appFilterOptions]);

    const selectFilterValue = effectiveFilterApp === '' ? FILTER_ALL_VALUE : effectiveFilterApp;

    /** Keep state aligned when `user.apps` no longer includes the selected code */
    useEffect(() => {
        if (filterApp !== '' && !appFilterOptions.some((o) => o.code === filterApp)) {
            setFilterApp('');
        }
    }, [filterApp, appFilterOptions]);

    const loadNotifications = useCallback(async () => {
        if (!apiBaseUrl || !bearerToken) {
            setNotifications([]);
            setListStatus('idle');
            setListError(null);
            return;
        }
        setListStatus('loading');
        setListError(null);
        try {
            const qs = notificationsListQuery(effectiveFilterApp);
            const res = await fetch(`${apiBaseUrl}/api/notifications${qs}`, {
                headers: notificationsApiHeaders(bearerToken),
            });
            const body = await parseNotificationsApiJson<{ data?: ToolbarNotification[]; message?: string }>(res);
            if (!res.ok) {
                setListStatus('error');
                setListError(body?.message ?? `HTTP ${res.status}`);
                setNotifications([]);
                return;
            }
            setNotifications(Array.isArray(body.data) ? body.data : []);
            setListStatus('ok');
        } catch (e) {
            setListStatus('error');
            setListError(e instanceof Error ? e.message : String(e));
            setNotifications([]);
        }
    }, [apiBaseUrl, bearerToken, effectiveFilterApp]);

    const fetchUnreadNotificationsCount = useCallback(async () => {
        if (!apiBaseUrl || !bearerToken) {
            setUnreadCount(null);
            return;
        }
        try {
            const res = await fetch(`${apiBaseUrl}/api/notifications/informations`, {
                headers: notificationsApiHeaders(bearerToken),
            });
            const body = await parseNotificationsApiJson<{ data?: { unread_notifications_count?: number } }>(res);
            if (!res.ok) return;
            const raw = body.data?.unread_notifications_count;
            setUnreadCount(typeof raw === 'number' && Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0);
        } catch {
            // Keep prior badge; avoid flashing empty on transient errors
        }
    }, [apiBaseUrl, bearerToken]);

    useEffect(() => {
        void fetchUnreadNotificationsCount();
    }, [fetchUnreadNotificationsCount]);

    const markAllNotificationsRead = useCallback(async () => {
        if (!apiBaseUrl || !bearerToken) return;
        try {
            await fetch(`${apiBaseUrl}/api/notifications/all/read`, {
                method: 'PUT',
                headers: notificationsApiHeaders(bearerToken),
            });
        } catch {
            // Drawer still opens; list load may show prior read state
        }
    }, [apiBaseUrl, bearerToken]);

    const openRef = useRef(open);
    openRef.current = open;

    const onRealtimeNotification = useCallback(
        (e: ToolbarNotification) => {
            if (openRef.current) {
                setNotifications((prev) => dedupeNotificationsById([e, ...prev]));
            }
            void fetchUnreadNotificationsCount();
        },
        [fetchUnreadNotificationsCount],
    );

    const echoListenerActive = Boolean(echoParams && uid != null && String(uid).trim() !== '');

    useEffect(() => {
        if (!open) return;
        if (apiBaseUrl && bearerToken) {
            setListStatus('loading');
            setListError(null);
        }
        void (async () => {
            await loadNotifications();
            if (!openRef.current) return;
            await markAllNotificationsRead();
            if (!openRef.current) return;
            await fetchUnreadNotificationsCount();
        })();
    }, [open, loadNotifications, markAllNotificationsRead, fetchUnreadNotificationsCount]);

    const badgeLabel = unreadCount !== null && unreadCount >= 9 ? '+9' : unreadCount !== null && unreadCount > 0 ? String(unreadCount) : null;
    const bellAriaLabel = unreadCount !== null && unreadCount > 0 ? `Notificações, ${unreadCount >= 9 ? 'mais de nove' : unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}` : 'Notificações';

    if (hideApps) return null;

    return (
        <>
            <Button type='button' variant='ghost' size='icon' className='relative shrink-0 [&>svg]:size-4' aria-label={bellAriaLabel} title='Notificações' onClick={() => setOpen(true)}>
                <span className='relative inline-flex'>
                    <Bell size={18} aria-hidden />
                    {badgeLabel !== null && (
                        <span className='pointer-events-none absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-purple-600 px-1 text-[10px] font-bold leading-none text-white tabular-nums' aria-hidden>
                            {badgeLabel}
                        </span>
                    )}
                </span>
            </Button>

            {echoListenerActive && echoParams && (
                <ToolbarEchoGate params={echoParams}>
                    <ToolbarNotificationsBroadcast appName={channelAppKey} userId={uid!} onNotification={onRealtimeNotification} />
                </ToolbarEchoGate>
            )}

            <Drawer open={open} onOpenChange={setOpen} direction='right'>
                <DrawerContent className='!w-[min(420px,100vw)] !max-w-[min(420px,100vw)] border-l border-gray-200 p-0'>
                    <DrawerHeader className='border-b border-gray-200 bg-white px-4 py-4'>
                        <div className='flex items-start justify-between gap-2'>
                            <div className='min-w-0 space-y-1'>
                                <DrawerTitle className='text-xl font-bold tracking-tight text-gray-900'>Notificações</DrawerTitle>

                                <DrawerDescription className='text-xs text-gray-500'>Veja suas notificações</DrawerDescription>
                            </div>
                        </div>
                    </DrawerHeader>

                    <div className='flex h-[calc(100vh-88px)] flex-col gap-3 overflow-y-auto px-4 py-4 custom-scroll'>
                        <div className='flex flex-col gap-1.5'>
                            <span className='text-xs font-medium text-gray-700'>Filtrar por aplicativo</span>
                            <Select value={selectFilterValue} onValueChange={(v) => setFilterApp(v === FILTER_ALL_VALUE ? '' : v)}>
                                <SelectTrigger className='h-10 w-full bg-white text-left' aria-label='Filtrar notificações por aplicativo'>
                                    <SelectValue placeholder='Todas' />
                                </SelectTrigger>
                                <SelectContent className='z-[200]' position='popper' sideOffset={4}>
                                    <SelectItem value={FILTER_ALL_VALUE}>
                                        <SelectIcon>
                                            <LayoutGrid className='size-4 text-gray-500' aria-hidden />
                                        </SelectIcon>
                                        Todos os aplicativos
                                    </SelectItem>
                                    {appFilterOptions.map(({ code, label, iconSrc }) => (
                                        <SelectItem key={code} value={code}>
                                            <SelectIcon>
                                                {iconSrc ? (
                                                    <img src={iconSrc} alt='' className='size-4 object-contain' />
                                                ) : (
                                                    <span className='flex size-4 items-center justify-center rounded bg-gray-100 text-[10px] font-semibold text-gray-500' aria-hidden>
                                                        {label.slice(0, 1).toUpperCase()}
                                                    </span>
                                                )}
                                            </SelectIcon>
                                            <span className='min-w-0 truncate' title={label}>
                                                {label}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {listStatus === 'error' && listError && <p className='text-sm text-red-600'>{listError}</p>}

                        <div className='relative min-h-0 w-full'>
                            <AnimatePresence mode='wait' initial={false}>
                                {listStatus === 'loading' && apiBaseUrl && bearerToken ? (
                                    <motion.div key='notifications-skeleton' className='w-full' {...NOTIFICATIONS_LIST_MOTION}>
                                        <NotificationsListSkeleton />
                                    </motion.div>
                                ) : (
                                    <motion.div key={`notifications-panel-${effectiveFilterApp || FILTER_ALL_VALUE}-${listStatus}`} className='w-full' {...NOTIFICATIONS_LIST_MOTION}>
                                        <ToolbarNotificationsPanel
                                            initialNotifications={notifications}
                                            filterApplicationCode={effectiveFilterApp}
                                            appName={channelAppKey}
                                            userId={uid}
                                            echoParams={echoParams}
                                            appsConfig={appsConfig}
                                            translate={(key) => key}
                                            resolveAsset={(path) => path}
                                            subscribeToEcho={false}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </DrawerContent>
            </Drawer>
        </>
    );
}
